import { debounce } from 'lodash';
import * as io from 'socket.io-client';
import { default as Cookies } from 'universal-cookie';
import { v4 } from 'uuid';
import packageJson from '../../package.json';
import { handleSocketBroadcasts } from '../commands/broadcastHandler';
import { checkFeatureFlags } from '../commands/featureFlagsCommands';
import { checkMaintenanceStatus } from '../commands/maintenanceCommands';
import { menuMain } from '../commands/menuCommands';
import { unlockSession } from '../commands/userCommands';
import { ERROR_TYPE } from '../data-types/error/errorEnums';
import { auth } from '../data-types/initialisation/initialisationConstants';
import { Query, SocketError, SocketMessage } from '../data-types/initialisation/initialisationInterfaces';
import { handleBrandRedirect, handleSettingsFetch, setBetkwiffFlag, setGeoLocationInfo } from '../international/internationalUtils';
import { resetState } from '../state/actions/stateActions';
import { setAuthentication } from '../state/actions/userDataActions';
import { isLocalhost, isProductionEnv } from '../utils/general';
import { getCookie, setCookie } from '../utils/incomeAccess';
import { tryToInitiatePostHog } from '../utils/posthog';

export let cookies: Cookies;
export let fingerprint: string;
export let device: string;
export let socket: SocketIOClient.Socket;
let currentManualRetryAttempts: number = 0;
let isFirstLoad = true;

export const KWIFF_DOMAINS = JSON.parse(process.env.REACT_APP_KWIFF_DOMAINS || '[]');
export const IS_PROD_ENV = Boolean(JSON.parse(process.env.REACT_APP_IS_PROD || 'false'));
export const GTM_KEY = process.env.REACT_APP_GOOGLE_TAG_MANAGER_API_KEY || undefined;
export const RECAPTCHA_PUBLIC_KEY = process.env.REACT_APP_RECAPTCHA_KEY || undefined;
export const RECAPTCHA_DISABLED = Boolean(JSON.parse(process.env.REACT_APP_RECAPTCHA_DISABLED || 'false'));
export const POST_HOG_ENABLED = Boolean(JSON.parse(process.env.REACT_APP_POST_HOG_ENABLED || 'false'));
export const HORSE_STREAMING_ENABLED = Boolean(JSON.parse(process.env.REACT_APP_HORSE_STREAMING_ENABLED || 'false'));
export const GREYHOUND_STREAMING_ENABLED = Boolean(JSON.parse(process.env.REACT_APP_GREYHOUND_STREAMING_ENABLED || 'false'));
export const BETRADAR_STREAMING_ENABLED = Boolean(JSON.parse(process.env.REACT_APP_BETRADAR_STREAMING_ENABLED || 'false'));
export const POSTCODER_API_KEY = process.env.REACT_APP_POSTCODER_API_KEY || undefined;
export const VALIDATE_EMAIL_WITH_POSTCODER = Boolean(JSON.parse(process.env.REACT_APP_VALIDATE_EMAIL_WITH_POSTCODER || 'false'));
export const ENABLE_LMT_IN_MY_BETS = Boolean(JSON.parse(process.env.REACT_APP_ENABLE_LMT_IN_MY_BETS || 'false'));
export const TWITTER_PIXEL_ENABLED = Boolean(JSON.parse(process.env.REACT_APP_TWITTER_PIXEL_ENABLED || 'false'));
export const TWITTER_PIXEL_DEPOSIT_KEY = process.env.REACT_APP_TWITTER_PIXEL_DEPOSIT_KEY || undefined;
export const TWITTER_PIXEL_FIRST_DEPOSIT_KEY = process.env.REACT_APP_TWITTER_PIXEL_FIRST_DEPOSIT_KEY || undefined;
export const TWITTER_PIXEL_COMPLETE_REGISTRATION_KEY = process.env.REACT_APP_TWITTER_PIXEL_COMPLETE_REGISTRATION_KEY || undefined;
export const TWITTER_PIXEL_START_REGISTRATION_KEY = process.env.REACT_APP_TWITTER_PIXEL_START_REGISTRATION_KEY || undefined;
export const TWITTER_PIXEL_LOGIN_KEY = process.env.REACT_APP_TWITTER_PIXEL_LOGIN_KEY || undefined;
export const TWITTER_PIXEL_PAGE_VIEW_KEY = process.env.REACT_APP_TWITTER_PIXEL_PAGE_VIEW_KEY || undefined;
export const AVIATOR_GAME_ID = Number(JSON.parse(process.env.REACT_APP_AVIATOR_GAME_ID || '""')) || undefined;
export const AVIATOR_ALLOWED_IN_MENU = Boolean(JSON.parse(process.env.REACT_APP_ALLOW_AVIATOR_IN_MENU || 'false'));
export const SPORTRADAR_PIXEL_ENABLED = Boolean(JSON.parse(process.env.REACT_APP_SPORTRADAR_PIXEL_ENABLED || 'false'));
export const INCOME_ACCESS_PIXEL_ENABLED = Boolean(JSON.parse(process.env.REACT_APP_INCOME_ACCESS_PIXEL_ENABLED || 'false'));
export const XTREMEPUSH_API_KEY = process.env.REACT_APP_XTREMEPUSH_API_KEY || undefined;
export const COMBO_BETS_ENABLED = Boolean(JSON.parse(process.env.REACT_APP_COMBO_BETS_ENABLED || 'false'));
export const APP_TOUR_ENABLED = Boolean(JSON.parse(process.env.REACT_APP_APP_TOUR_ENABLED || 'false'));
export const ZENDESK_DISABLE_IN_COUNTRIES = JSON.parse(process.env.REACT_APP_ZENDESK_DISABLE_IN_COUNTRIES || '[]');
export const PROJECT_PHOENIX_DOMAIN_ORIGIN = process.env.REACT_APP_PROJECT_PHOENIX_DOMAIN_ORIGIN || '';

// The socket address the web-app will connect to
export const SOCKET_ADDRESS = process.env.REACT_APP_WEBSOCKET || 'ws://localhost:5000';
const MAX_MANUAL_RETRY_ATTEMPTS = 20;

const MANUAL_RETRY_INTERVAL = 2000; // time in milliseconds

const getDevice = () => {
  const check = navigator.userAgent.toLowerCase().match(/android|iphone|ipad/i);
  if (check && ['iphone', 'ipad'].includes(check[0])) {
    return 'iPhone';
  }
  if (check && check[0] === 'android') {
    return 'Android';
  }
  // Business decision to default to android as it is easier to see the effect as it is a smaller platform
  return 'Android';
};

const handleSocketError = (error: SocketError) => {
  if (error.code === ERROR_TYPE.LOCATION_REJECTED || error.code === ERROR_TYPE.LOCATION_INVALID) {
    setAuthentication(auth.LOCATION_REJECTED);
  } else if (error.code === ERROR_TYPE.REDIRECT_REQUIRED && error.payload && error.payload.from && error.payload.redirectTo) {
    handleBrandRedirect(error.payload.from, error.payload.redirectTo);
  } else {
    debounceRetryToConnect();
  }
};

/**
 * Allow wait for the connection to be fully established
 */
const checkIfConnected = (): Promise<boolean> =>
  new Promise((resolve, reject) => {
    socket.on('connect', resolve.bind(null, true));
    socket.on('connect_error', reject.bind(null, false)); // TODO: error handling.
    socket.on('error', reject.bind(null, false)); // TODO: error handling.
  });

export const retryToConnect = (): Promise<boolean> =>
  new Promise(resolve => {
    if (currentManualRetryAttempts < MAX_MANUAL_RETRY_ATTEMPTS) {
      currentManualRetryAttempts += 1;

      const connectCallback = async () => {
        clearCallbacks();
        let result;

        try {
          result = await unlockSession();
        } catch {
          result = false;
        }

        resolve(result);
      };

      const errorCallback = () => {
        clearCallbacks();
        resolve(false);
      };

      const clearCallbacks = () => {
        socket.off('connect', connectCallback);
        socket.off('error', errorCallback);
        socket.off('connect_error', errorCallback);
      };

      socket.on('connect', connectCallback);
      socket.on('error', errorCallback);
      socket.on('connect_error', errorCallback);

      socket.connect();
    } else {
      socket.disconnect();
      setAuthentication(auth.UNAUTHENTICATED);
      resolve(false);
    }
  });

export const debounceRetryToConnect = debounce(retryToConnect, MANUAL_RETRY_INTERVAL, { leading: true });

const getFingerprint = () => {
  const cookieUUID = getCookie('uuid') || '';

  if (cookieUUID) {
    return cookieUUID;
  }

  const v4UUID = v4();
  setCookie('uuid', v4UUID);
  return v4UUID;
};

/**
 * Initialise the socket connection
 */
export const initialise = async (): Promise<boolean> => {
  if (!socket) {
    cookies = new Cookies();

    if (!isProductionEnv()) {
      fingerprint = v4();
      localStorage.setItem('uuid', fingerprint);
    } else {
      fingerprint = getFingerprint();
    }

    device = getDevice();

    const query: Query = generateSocketQuery();

    tryToInitiatePostHog();

    try {
      socket = connectSocket(query);
      socket.open();

      socket.on('connect_error', () => {
        debounceRetryToConnect();
      });

      socket.on('error', (e: SocketError) => {
        handleSocketError(e);
      });

      socket.on('disconnect', () => {
        debounceRetryToConnect();
      });

      socket.on('connect', () => {
        handleSocketConnection();
      });

      socket.on('broadcast', (data: SocketMessage) => {
        handleSocketBroadcasts(data);
      });
    } catch (error) {
      if (error.code === 1) {
        setAuthentication(auth.LOCATION_DENIED);
        return false;
      }
      throw new Error('Something went wrong!');
    }

    let connectionSuccessful = false;
    try {
      connectionSuccessful = await checkIfConnected();
    } catch (e) {
      return false;
    }
    if (!connectionSuccessful) {
      return false;
    }
  }

  if (localStorage.sessionToken) {
    try {
      setBetkwiffFlag();
      await unlockSession();
    } catch {
      resetState();
      setBetkwiffFlag();
      await setGeoLocationInfo();
      handleSettingsFetch();
    }
  } else {
    resetState();
    setBetkwiffFlag();
    await setGeoLocationInfo();
    handleSettingsFetch();
  }

  return true;
};

const generateSocketQuery = (): Query => ({
  device: 'web-app',
  version: packageJson.version,
  identifier: fingerprint,
});

const connectSocket = (query: Query) => {
  const socketAddress = `${SOCKET_ADDRESS}${
    isLocalhost() ? '?device=web-app&identifier=the-iron-curtain&version=1.0.1&domain=kwiff.org' : ''
  }`;

  return io.connect(socketAddress, {
    query,
    transports: ['websocket'],
    forceNew: true,
    secure: true,
    reconnectionAttempts: 5,
  });
};

const handleSocketConnection = () => {
  if (currentManualRetryAttempts > 0) {
    currentManualRetryAttempts = 0;
  }

  if (isFirstLoad) {
    isFirstLoad = false;
    handlePostInitTasks();
  }
};

const handlePostInitTasks = async () => {
  setBetkwiffFlag();

  let postInitFunctions = [setGeoLocationInfo, checkFeatureFlags];
  await Promise.all(postInitFunctions.map(initFunction => initFunction())).catch(() => {
    // do nothing
  });

  postInitFunctions = [checkMaintenanceStatus, menuMain];
  await Promise.all(postInitFunctions.map(initFunction => initFunction())).catch(() => {
    // do nothing
  });
};
